import classNames from 'classnames'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { useTranslations } from '../../hooks/useTranslations'
import { InternalExternalLink } from '../../types/InternalExternalLink'
import {
  isNullOrWhiteSpace,
  sanitizeHtmlAndReplaceNewlines,
} from '../../utilities/string'
import { Button } from '../Button'
import { Image, ImageProps } from '../Image'
import { AspectRatio } from '../VideoAspectRatio'
import { VideoComponent } from '../VideoComponent'

export interface ImageTextProps {
  image?: ImageProps
  isBackgroundImage?: boolean
  link?: InternalExternalLink
  subtitle?: string
  text?: string
  title?: string
  videoUrl?: string
}

const sharedBoxClasses = classNames(
  'w-330 rounded-15',
  'tb:w-[31.25rem]',
  'sm:w-[37.5rem]',
  'md:w-[46.875rem]',
  'lg:w-[52.5rem]',
  'xl:w-[65.625rem]'
)

export const ImageText = ({
  image,
  isBackgroundImage,
  link,
  subtitle,
  text,
  title,
  videoUrl,
}: ImageTextProps): JSX.Element => {
  const translations = useTranslations()

  let containerClassName = 'w-full relative flex flex-col items-center'
  let textContainerClassName = 'flex flex-col items-center'
  let imageClassName: string

  if (isBackgroundImage) {
    containerClassName = classNames(
      containerClassName,
      'px-30 py-45',
      'sm:py-120',
      'lg:py-150',
      'xl:py-240',
      'xxl:py-240'
    )

    textContainerClassName = classNames(
      textContainerClassName,
      sharedBoxClasses,
      'relative bg-neutral',
      'px-15 py-45',
      'tb:px-90 tb:py-45',
      'sm:px-150 sm:py-75',
      'md:py-120',
      'lg:px-150 lg:py-150',
      'xl:py-240',
      'xxl:w-[88.125rem] xxl:px-330 xxl:py-240'
    )

    imageClassName = 'absolute w-full h-full inset-0'
  } else {
    imageClassName = classNames(
      'mt-45 order-2',
      'sm:mt-75',
      'lg:mt-45',
      'xl:mt-90',
      sharedBoxClasses
    )
  }

  const resolvedButtonTitle =
    link && !isNullOrWhiteSpace(link.title) ? link.title : translations.more

  return (
    <div className={containerClassName}>
      {!isBackgroundImage && !isNullOrWhiteSpace(videoUrl) && (
        <InView triggerOnce rootMargin="0px 0px 500px 0px">
          {({ inView, ref }) => (
            <div
              className={classNames(imageClassName, 'overflow-hidden rounded-15')}
              ref={ref}
            >
              {inView && (
                <VideoComponent
                  aspectRatio={AspectRatio.Widescreen}
                  previewImage={image}
                  title={title}
                  videoUrl={videoUrl}
                />
              )}
            </div>
          )}
        </InView>
      )}

      {image && isNullOrWhiteSpace(videoUrl) && (
        <Image
          className={imageClassName}
          imgClassName={classNames('w-full h-full', {
            'rounded-15': !isBackgroundImage,
          })}
          {...image}
          caption=""
          copyright=""
        />
      )}

      {(!isNullOrWhiteSpace(title) ||
        !isNullOrWhiteSpace(subtitle) ||
        !isNullOrWhiteSpace(text)) && (
        <div className={textContainerClassName}>
          <div className={classNames('tb:w-330', 'md:w-480', 'xl:w-[43.125rem]')}>
            {!isNullOrWhiteSpace(title) && (
              <h2
                className={classNames(
                  'font-h2 text-h2 text-primary text-center',
                  'mb-30',
                  'sm:mb-15',
                  'md:mb-20'
                )}
              >
                {title}
              </h2>
            )}

            {!isNullOrWhiteSpace(subtitle) && (
              <h3 className="mb-30 font-h3 text-h3 text-secondary-dark text-center">
                {subtitle}
              </h3>
            )}

            {!isNullOrWhiteSpace(text) && (
              <p
                className={classNames(
                  'font-preamble text-preamble text-secondary-darker text-center',
                  'xxl:mx-90'
                )}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlAndReplaceNewlines(text),
                }}
              />
            )}
          </div>

          {link && !isNullOrWhiteSpace(link.url) && (
            <Button
              additionalClassName="mt-45 xxl:mt-90"
              customAttributes={link.customAttributes}
              isExternalUrl={link.isExternal}
              title={resolvedButtonTitle ?? ''}
              url={link.url}
            >
              {resolvedButtonTitle}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
